/*
 * Project: bellcloud
 * File: packages/utils/device/converters/payloadCodec.ts
 * Author: SWAN
 * Created: 2025-11-03
 * Description: 2단계 파이프라인으로 패킷과 JSON 간 변환을 통합 관리
 */

import type { FunctionParameter } from '../../../protocols/fns';
import type { DeviceInfo } from '../../../types/device/lite/deviceInfo';
import { DeviceType, type ProtocolType } from '../../../types/device/common/deviceTypes';
import { SensorType } from '../../../types/device/lite/sensors';
import { TypeConverter } from './wireTypeCodec';
import { FormatConverter } from './formatCodec';

/**
 * PacketConverter: 패킷과 JSON 간의 2단계 변환 파이프라인
 *
 * Packet → JSON:
 *   1. TypeConverter: bytes → raw value
 *   2. FormatConverter: raw value → formatted value
 *
 * JSON → Packet:
 *   1. FormatConverter: formatted value → raw value
 *   2. TypeConverter: raw value → bytes
 */
export class PacketConverter {
  /**
   * 패킷 페이로드를 JSON으로 변환
   * @param payload 패킷 페이로드 (바이트 배열)
   * @param paramDefs 파라미터 정의 배열
   * @param protocol `DeviceType`과 동일 (`belllite` / `bellmini`)
   * @param deviceInfo 디바이스 정보 (센서 타입 등)
   * @returns JSON 객체
   */
  static packetToJson(
    payload: number[],
    paramDefs: FunctionParameter[],
    protocol?: ProtocolType,
    deviceInfo?: DeviceInfo
  ): Record<string, unknown> {
    const json: Record<string, unknown> = {};
    let offset = 0;

    for (const param of paramDefs) {
      const { name, type, bytes, format } = param;

      // Extract raw bytes
      const rawBytes = payload.slice(offset, offset + bytes);

      // 1단계: 바이트 → 원시값 (타입 기반)
      const rawValue = TypeConverter.bytesToValue(rawBytes, type);

      // 2단계: 원시값 → 포맷값 (포맷 기반)
      let formattedValue: unknown;
      if (format) {
        // 센서 타입 정보 추출 (필요 시)
        let sensorType: SensorType | SensorType[] | undefined;
        if (deviceInfo?.sensor_type && Array.isArray(deviceInfo.sensor_type)) {
          if (name.includes('lel_CF_Index')) {
            // 센서별 배열이므로 센서 타입 배열 전달
            sensorType = deviceInfo.sensor_type.map(sensor => sensor.value as SensorType);
          } else if (name.includes('sensor')) {
            // 첫 번째 센서 타입 사용
            const firstSensor = deviceInfo.sensor_type[0];
            if (firstSensor) {
              sensorType = firstSensor.value as SensorType;
            }
          }
        }

        formattedValue = FormatConverter.applyFormat(rawValue, format, protocol, sensorType);
      } else {
        formattedValue = rawValue;
      }

      json[name] = formattedValue;

      // Advance offset
      offset += bytes;
    }

    return json;
  }

  /**
   * JSON을 패킷 페이로드로 변환
   * @param jsonData JSON 데이터
   * @param paramDefs 파라미터 정의 배열
   * @param protocol `DeviceType`과 동일 (`belllite` / `bellmini`)
   * @param deviceInfo 디바이스 정보 (센서 타입 등)
   * @returns 페이로드 바이트 배열
   */
  static jsonToPayload(
    jsonData: Record<string, unknown>,
    paramDefs: FunctionParameter[],
    protocol?: ProtocolType,
    deviceInfo?: { sensor_type?: Array<{ value: number; label: string }> }
  ): number[] {
    const payload: number[] = [];

    // O2/O2LF 센서의 bump_test_threshold를 40으로 강제 설정
    if (protocol === DeviceType.LITE && deviceInfo?.sensor_type && jsonData.bump_test_threshold) {
      const bumpTestThreshold = jsonData.bump_test_threshold as number[];
      const sensorTypes = deviceInfo.sensor_type;
      const fixedBumpTestThreshold = bumpTestThreshold.map((value, index) => {
        const sensorType = sensorTypes[index]?.value as SensorType;
        // O2 (6) 또는 O2LF (0)인 경우 40으로 고정
        if (sensorType === SensorType.O2 || sensorType === SensorType.O2LF) {
          return 40;
        }
        return value;
      });
      jsonData.bump_test_threshold = fixedBumpTestThreshold;
    }

    for (const param of paramDefs) {
      const { name, type, bytes, format } = param;
      const formattedValue = jsonData[name];

      if (formattedValue === undefined || formattedValue === null) {
        throw new Error(`Missing parameter: ${name}`);
      }

      // 1단계: 포맷값 → 원시값 (포맷 기반)
      let rawValue: unknown;
      if (format) {
        rawValue = FormatConverter.parseFormat(formattedValue, format);
      } else {
        rawValue = formattedValue;
      }

      // 2단계: 원시값 → 바이트 (타입 기반)
      const byteArray = TypeConverter.valueToBytes(rawValue, type, bytes);

      payload.push(...byteArray);
    }

    return payload;
  }
}
